import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';

const VALUES = [
  {
    icon: 'ri-focus-3-line',
    titleKey: 'about.mission.v1.title',
    textKey: 'about.mission.v1.text',
  },
  {
    icon: 'ri-lock-2-line',
    titleKey: 'about.mission.v2.title',
    textKey: 'about.mission.v2.text',
  },
  {
    icon: 'ri-stack-line',
    titleKey: 'about.mission.v3.title',
    textKey: 'about.mission.v3.text',
  },
  {
    icon: 'ri-team-line',
    titleKey: 'about.mission.v4.title',
    textKey: 'about.mission.v4.text',
  },
];

export default function AboutMissionSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-14 md:py-16 bg-background-50 overflow-hidden">
      <FloatingShapes count={14} color="secondary" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={8} color="accent" speed={0.5} />
      </div>

      <div className="absolute top-0 right-0 w-72 h-72 bg-secondary-500/6 rounded-full blur-3xl animate-[morphFloat_8s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl animate-[morphPulse_7s_ease-in-out_infinite_1s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-secondary-100/80 rounded-full text-[10px] font-mono font-medium text-secondary-700 uppercase tracking-widest mb-4">
              {t('about.mission.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('about.mission.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mt-4">
              {t('about.mission.paragraph')}
            </p>
          </div>

          {/* Values grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {VALUES.map((value, i) => (
              <div
                key={value.titleKey}
                className={`relative p-6 rounded-xl border border-background-200/80 bg-background-50/80 backdrop-blur-sm transition-all duration-700 hover:-translate-y-1 hover:border-accent-200/70 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                  <i className={`${value.icon} text-xl text-accent-600`} />
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                  {t(value.titleKey)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(value.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}